import { Link, useOutletContext } from 'react-router-dom';
import { BarChart3, ChevronDown, ChevronRight, FileText, GitCompare, Loader2, PencilLine } from 'lucide-react';
import { ProjectDetailSection } from '../../components/project-detail/ProjectDetailSection';
import { Tooltip } from '../../components/ui/Tooltip';
import { humanizeAggregateLabel, pricingLineTypeNl } from '../../lib/catalog-labels';
import {
  formatEuroNl,
  formatMeasureUnitNl,
  formatQuantityNl,
  humanizePricingLineDescription,
} from '../../lib/format-nl';
import type { ProjectDetailOutletContext } from './outletTypes';

function costNarrative(v: ProjectDetailOutletContext): string {
  if (!v.aggregation) return '';
  const n = v.aggregation.systems?.length ?? 0;
  const parts = v.aggregation.building_part_summary?.length ?? 0;
  const tone = v.customerUxProfile.resultaatSamenvattingTone;
  const clusterPhrase =
    v.customerUxProfile.resultDetailLevel === 'simple'
      ? ''
      : parts
        ? ` verdeeld over ${parts} cluster${parts === 1 ? '' : 's'}`
        : '';
  return `Dit dossier bevat ${n} begrotingsregel${n === 1 ? '' : 'en'}${clusterPhrase}. ${tone} De bedragen zijn een modelreferentie exclusief BTW, voor regio “${v.regionDisplayLabel}”. Geen vervanging voor een bindende aannemersofferte.`;
}

function isBreakdownOpen(v: ProjectDetailOutletContext, index: number): boolean {
  if (v.expandedBreakdown === -1) return false;
  if (v.expandedBreakdown === index) return true;
  if (v.expandedBreakdown === null && index === 0) return true;
  return false;
}

function toggleBreakdown(v: ProjectDetailOutletContext, index: number) {
  const open = isBreakdownOpen(v, index);
  if (open) v.setExpandedBreakdown(-1);
  else v.setExpandedBreakdown(index);
}

export function ResultaatTab() {
  const v = useOutletContext<ProjectDetailOutletContext>();

  if (v.aggregationLoading && v.hasSystems) {
    return (
      <div
        className="mb-6 flex items-center gap-2 rounded-xl border border-border bg-surface-card px-4 py-3 text-sm text-text-muted"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
        Berekening laden…
      </div>
    );
  }

  if (!v.aggregationLoading && v.hasSystems && !v.aggregation && v.aggregationError) {
    return (
      <div
        className="mb-6 rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
        role="alert"
      >
        <strong className="block text-base">Aggregatie mislukt</strong>
        <p className="mt-2">{v.aggregationError}</p>
        <p className="mt-3 text-xs opacity-90">
          Controleer je posten en context (Gegevens), of probeer later opnieuw.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to={`/projects/${v.projectId}/posten`}
            className="font-medium text-red-900 underline dark:text-red-100"
          >
            Naar posten →
          </Link>
          <Link
            to={`/projects/${v.projectId}/gegevens`}
            className="font-medium text-red-900 underline dark:text-red-100"
          >
            Naar gegevens →
          </Link>
        </div>
      </div>
    );
  }

  if (!v.hasSystems) {
    return (
      <div
        className="flex flex-col items-center rounded-xl border border-dashed border-border bg-surface-card/80 px-6 py-10 text-center"
        role="status"
      >
        <BarChart3 className="mb-4 h-12 w-12 text-text-muted/40" aria-hidden />
        <h2 className="mb-2 text-lg font-semibold text-text">Nog geen totalen</h2>
        <p className="mb-4 max-w-md text-sm text-text-muted">
          Sla eerst begrotingsregels op bij Posten (knop <strong className="text-text">Regels opslaan</strong>). Daarna
          verschijnen hier de doorrekening en onderbouwing.
        </p>
        <Link
          to={`/projects/${v.projectId}/posten`}
          className="font-medium text-primary hover:underline"
        >
          Naar posten →
        </Link>
      </div>
    );
  }

  if (!v.aggregation) return null;

  const sysCount = v.aggregation.systems?.length ?? 0;
  const prof = v.customerUxProfile;
  const simpleSummary = prof.resultDetailLevel === 'simple';

  if (sysCount === 0) {
    return (
      <div
        className="rounded-xl border border-amber-200/80 bg-amber-50/80 p-6 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
        role="status"
      >
        <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
          <BarChart3 className="mb-3 h-10 w-10 text-amber-700/70 dark:text-amber-200/80" aria-hidden />
          <p className="font-medium text-text">Geen regels in deze berekening</p>
          <p className="mt-2 max-w-lg text-text-muted">
            De server heeft geen onderbouwde posten voor dit dossier. Ga naar Posten, controleer je regels en kies{' '}
            <strong className="text-text">Regels opslaan</strong>.
          </p>
          <Link
            to={`/projects/${v.projectId}/posten`}
            className="mt-4 inline-flex font-medium text-primary hover:underline"
          >
            Naar posten →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ProjectDetailSection
        id="kosten-totalen"
        icon={BarChart3}
        variant="highlight"
        title="Samenvatting kosten"
        description={
          simpleSummary
            ? 'Overzicht van het totaal — details staan per regel hieronder als je die openklapt.'
            : 'Totalen op hoofdlijnen — klap per regel hieronder open voor materialen, arbeid en materieel.'
        }
      >
        <div className="mb-5 rounded-xl border border-primary/15 bg-primary/[0.06] px-4 py-3 text-sm leading-relaxed text-text">
          <p className="whitespace-pre-wrap text-text [text-wrap:pretty]">{costNarrative(v)}</p>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2">
          {!simpleSummary && (
            <>
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-surface/80 px-4 py-3">
                <dt className="flex items-center gap-1 text-sm text-text-muted">
                  Materialen
                  <Tooltip
                    iconOnly
                    label="Uitleg materialen"
                    content="Som van geprijsde materialen uit de library-normen, na regio en index (achter de schermen)."
                  />
                </dt>
                <dd className="text-sm font-semibold tabular-nums text-text">{formatEuroNl(v.aggregation.total_materials)}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-surface/80 px-4 py-3">
                <dt className="flex items-center gap-1 text-sm text-text-muted">
                  Arbeid
                  <Tooltip
                    iconOnly
                    label="Uitleg arbeid"
                    content="Uren en tarieven uit het model, verdisconteerd in de normregels."
                  />
                </dt>
                <dd className="text-sm font-semibold tabular-nums text-text">{formatEuroNl(v.aggregation.total_labor)}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-surface/80 px-4 py-3">
                <dt className="flex items-center gap-1 text-sm text-text-muted">
                  Materieel
                  <Tooltip
                    iconOnly
                    label="Uitleg materieel"
                    content="Machines, gereedschap en huurposten die in de library-norm zijn meegenomen (geen materiaal en geen pure arbeidsuren)."
                  />
                </dt>
                <dd className="text-sm font-semibold tabular-nums text-text">{formatEuroNl(v.aggregation.total_equipment)}</dd>
              </div>
            </>
          )}
          <div
            className={`flex items-center justify-between gap-4 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 ${simpleSummary ? '' : 'sm:col-span-2'}`}
          >
            <dt className="flex items-center gap-1 text-base font-semibold text-text">
              Totaal (excl. BTW)
              <Tooltip
                iconOnly
                label="Uitleg totaal"
                content="Inclusief toegepaste praktische correctie (meerwerk/verspilling) op het gehele dossier."
              />
            </dt>
            <dd className="text-xl font-bold tabular-nums text-primary">{formatEuroNl(v.aggregation.total)}</dd>
          </div>
          {prof.showCo2InResultaat &&
            v.aggregation.total_co2_kg != null &&
            v.aggregation.total_co2_kg > 0 && (
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 bg-surface/80 px-4 py-3 sm:col-span-2">
              <dt className="text-sm text-text-muted">CO₂ (indicatief, materiaal)</dt>
              <dd className="text-sm font-semibold tabular-nums text-text">
                {v.aggregation.total_co2_kg.toFixed(0)} kg
              </dd>
            </div>
          )}
        </dl>
        <p className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-border/70 bg-surface px-3 py-2 text-sm text-text-muted">
          <GitCompare className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <span>
            <strong className="text-text">Scenario&apos;s vergelijken?</strong> Ga naar tab{' '}
            <Link to={`/projects/${v.projectId}/rapportage`} className="font-medium text-primary hover:underline">
              Rapportage
            </Link>{' '}
            — daar stel je alternatieve hoeveelheden in en zie je het verschil in totaal
            {prof.showCo2InResultaat ? ' en CO₂-indicatie' : ''}.
          </span>
        </p>
        {!simpleSummary &&
          v.aggregation.building_part_summary &&
          v.aggregation.building_part_summary.length > 0 && (
          <div className="mt-6 border-t border-border/70 pt-5">
            <p className="mb-1 text-sm font-semibold text-text">Per deel</p>
            <p className="mb-3 text-xs text-text-muted">
              Eigen clusters via het veld <strong className="text-text">Bouwcluster</strong> bij posten; anders op
              discipline uit de library. Correcties proportioneel verdeeld.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {v.aggregation.building_part_summary.map((p) => (
                <div
                  key={p.part_key}
                  className="rounded-xl border border-border/80 bg-surface/90 p-4 shadow-sm"
                >
                  <p className="text-sm font-semibold text-text">{humanizeAggregateLabel(p.part_label)}</p>
                  <dl className="mt-2 space-y-1.5 text-xs text-text-muted">
                    <div className="flex justify-between gap-2">
                      <dt>Materialen</dt>
                      <dd className="tabular-nums text-text">{formatEuroNl(p.total_materials)}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt>Arbeid</dt>
                      <dd className="tabular-nums text-text">{formatEuroNl(p.total_labor)}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt>Materieel</dt>
                      <dd className="tabular-nums text-text">{formatEuroNl(p.total_equipment)}</dd>
                    </div>
                    <div className="flex justify-between gap-2 border-t border-border/50 pt-1.5">
                      <dt>Correctie (toewijzing)</dt>
                      <dd className="tabular-nums text-text">{formatEuroNl(p.practical_corrections_allocated)}</dd>
                    </div>
                    <div className="flex justify-between gap-2 font-semibold text-primary">
                      <dt>Totaal deel</dt>
                      <dd className="tabular-nums">{formatEuroNl(p.total_with_corrections)}</dd>
                    </div>
                    {prof.showCo2InResultaat && p.total_co2_kg != null && p.total_co2_kg > 0 && (
                      <div className="flex justify-between gap-2 pt-1 text-text-muted">
                        <dt>CO₂ indicatief</dt>
                        <dd className="tabular-nums text-text">{p.total_co2_kg.toFixed(0)} kg</dd>
                      </div>
                    )}
                  </dl>
                </div>
              ))}
            </div>
          </div>
        )}
      </ProjectDetailSection>

      <ProjectDetailSection
        id="kosten-onderbouwing"
        icon={FileText}
        title="Onderbouwing per regel"
        description={
          <>
            Hier zie je de <strong className="text-text">prijsopbouw</strong> per begrotingsregel: in de tabel staan
            per detailregel het <strong className="text-text">soort</strong> (materiaal, arbeid, materieel), de hoeveelheid
            en eenheid, en de bedragen. <strong className="text-text">Materieel</strong> zijn o.a. machines, gereedschap en
            huurposten uit de norm — geen bouwmaterialen en geen losse arbeidscomponent. Klik een regel om te openen. Wil je
            iets wijzigen? Gebruik <strong className="text-text">Aanpassen in posten</strong>.
          </>
        }
      >
        <ul className="space-y-2">
          {(v.aggregation.systems ?? []).map((s, i) => {
            const open = isBreakdownOpen(v, i);
            const lines = s.pricing?.lines ?? [];
            const postenHref = s.line_id
              ? `/projects/${v.projectId}/posten?line=${encodeURIComponent(s.line_id)}`
              : `/projects/${v.projectId}/posten`;
            const sm = s.pricing?.subtotal_materials;
            const sl = s.pricing?.subtotal_labor;
            const se = s.pricing?.subtotal_equipment;
            return (
              <li
                key={`${s.line_id ?? ''}-${s.system_id}-${s.variant_id}-${i}`}
                className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm"
              >
                <div className="flex w-full flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => toggleBreakdown(v, i)}
                    className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left text-sm font-medium text-text transition hover:bg-surface-card sm:justify-start"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      {open ? (
                        <ChevronDown className="h-4 w-4 shrink-0 text-primary" />
                      ) : (
                        <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" />
                      )}
                      <span className="truncate">
                        <span className="mr-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                          {humanizeAggregateLabel(s.part_label ?? '—')}
                        </span>
                        {humanizeAggregateLabel(s.system_id)} / {humanizeAggregateLabel(s.variant_id)} —{' '}
                        {formatQuantityNl(Number(s.quantity))}×
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums text-primary sm:ml-auto">
                      {formatEuroNl(s.subtotal)}
                    </span>
                  </button>
                  <Link
                    to={postenHref}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface-card px-3 py-1.5 text-xs font-medium text-primary hover:bg-surface"
                  >
                    <PencilLine className="h-3.5 w-3.5" aria-hidden />
                    Aanpassen in posten
                  </Link>
                </div>
                {(sm != null || sl != null || se != null) && prof.showPerLineCostSplit && (
                  <div className="border-t border-border/60 bg-surface-card/40 px-4 py-2 text-xs text-text-muted">
                    <dl className="flex flex-wrap gap-x-4 gap-y-1">
                      {sm != null && (
                        <div className="flex gap-2">
                          <dt>Materialen</dt>
                          <dd className="tabular-nums text-text">{formatEuroNl(sm)}</dd>
                        </div>
                      )}
                      {sl != null && (
                        <div className="flex gap-2">
                          <dt>Arbeid</dt>
                          <dd className="tabular-nums text-text">{formatEuroNl(sl)}</dd>
                        </div>
                      )}
                      {se != null && (
                        <div className="flex gap-2">
                          <dt>Materieel</dt>
                          <dd className="tabular-nums text-text">{formatEuroNl(se)}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}
                {open && lines.length > 0 && (
                  <div className="border-t border-border bg-surface-card/50 px-2 pb-3 pt-2 sm:px-4">
                    <div className="overflow-x-auto rounded-lg border border-border/50 bg-surface">
                      <table className="w-full min-w-[min(100%,560px)] text-left text-xs sm:min-w-[640px]">
                        <thead>
                          <tr className="border-b border-border text-text-muted">
                            <th className="px-2 py-2 font-medium">Post / omschrijving</th>
                            <th className="py-2 pr-2 font-medium">Soort</th>
                            <th className="py-2 pr-2 font-medium">Aantal</th>
                            <th className="py-2 pr-2 font-medium">Eenheid</th>
                            <th className="py-2 pr-2 font-medium">Stuk</th>
                            <th className="px-2 py-2 font-medium">Totaal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.map((line, j) => (
                            <tr key={j} className="border-t border-border/50 text-text">
                              <td className="px-2 py-1.5 pr-2">{humanizePricingLineDescription(line.description)}</td>
                              <td className="py-1.5 pr-2 text-text-muted">{pricingLineTypeNl(line.type)}</td>
                              <td className="py-1.5 pr-2 tabular-nums">{formatQuantityNl(line.quantity)}</td>
                              <td className="py-1.5 pr-2">{formatMeasureUnitNl(line.unit)}</td>
                              <td className="py-1.5 pr-2 tabular-nums">{formatEuroNl(line.unit_price)}</td>
                              <td className="px-2 py-1.5 font-medium tabular-nums">{formatEuroNl(line.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {open && lines.length === 0 && (
                  <p className="border-t border-border px-4 py-3 text-xs text-text-muted">
                    Geen detailregels voor deze post. Controleer systeem/variant in Posten.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </ProjectDetailSection>
    </>
  );
}
